import React, { useState, useMemo, useEffect } from "react";
import { Capacitor } from '@capacitor/core';
import {
  Container,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Paper,
  Divider,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Button,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import SpeedIcon from "@mui/icons-material/Speed";
import HistoryIcon from "@mui/icons-material/History";
import StarIcon from "@mui/icons-material/Star";
import LockIcon from "@mui/icons-material/Lock";
import PremiumUpsellDialog from "../../components/PremiumUpsellDialog";

const GlossaryPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedLetter, setSelectedLetter] = useState("all");
  const [bookmarkedTerms, setBookmarkedTerms] = useState(new Set());
  const [recentTerms, setRecentTerms] = useState([]);

  // Speech synthesis state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentSpeakingTerm, setCurrentSpeakingTerm] = useState(null);
  const [speechRate, setSpeechRate] = useState(0.8);
  const [isPaused, setIsPaused] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Premium upsell state
  const [showUpsellDialog, setShowUpsellDialog] = useState(false);
  const [upsellTrigger, setUpsellTrigger] = useState("general");
  const [isPremium, setIsPremium] = useState(false); // TODO: Get from user context/auth
  const [audioPlaysToday, setAudioPlaysToday] = useState(0);

  // Free tier limits
  const FREE_AUDIO_LIMIT = 10;
  const FREE_BOOKMARK_LIMIT = 10;
  const FREE_TERMS_LIMIT = 100;

  // Check for speech synthesis support AND load voices
  useEffect(() => {
    if (!("speechSynthesis" in window)) {
      setSpeechSupported(false);
      console.warn("Web Speech API not supported in this browser");
      return;
    }

    console.log("🔊 Web Speech API supported!");

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        setVoicesLoaded(true);
        console.log("✅ Voices loaded:", voices.length);
        console.log(
          "Available voices:",
          voices.map((v) => `${v.name} (${v.lang})`).join(", ")
        );
      } else {
        console.log("⏳ Waiting for voices to load...");
      }
    };

    loadVoices();

    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  // Load bookmarks from localStorage
  useEffect(() => {
    const savedBookmarks = localStorage.getItem("glossaryBookmarks");
    if (savedBookmarks) {
      try {
        setBookmarkedTerms(new Set(JSON.parse(savedBookmarks)));
      } catch (e) {
        console.error("Error loading bookmarks:", e);
      }
    }

    const savedRecent = localStorage.getItem("recentGlossaryTerms");
    if (savedRecent) {
      try {
        setRecentTerms(JSON.parse(savedRecent));
      } catch (e) {
        console.error("Error loading recent terms:", e);
      }
    }
  }, []);

  // Save bookmarks to localStorage
  useEffect(() => {
    localStorage.setItem(
      "glossaryBookmarks",
      JSON.stringify([...bookmarkedTerms])
    );
  }, [bookmarkedTerms]);

  // Track daily audio plays
  useEffect(() => {
    const today = new Date().toDateString();
    const storedData = localStorage.getItem("audioPlaysData");

    if (storedData) {
      const { date, count } = JSON.parse(storedData);
      if (date === today) {
        setAudioPlaysToday(count);
      } else {
        localStorage.setItem(
          "audioPlaysData",
          JSON.stringify({ date: today, count: 0 })
        );
        setAudioPlaysToday(0);
      }
    } else {
      localStorage.setItem(
        "audioPlaysData",
        JSON.stringify({ date: today, count: 0 })
      );
    }
  }, []);

  // Glossary Data
  const glossaryData = [
    // Amyloidosis Types
    {
      term: "AL Amyloidosis",
      category: "Amyloidosis Types",
      definition:
        "Light chain (AL) amyloidosis is caused by an abnormality of plasma cells in the bone marrow. The plasma cells produce abnormal light chain proteins that misfold and deposit as amyloid in organs.",
      pronunciation: "ay-el am-uh-loy-DOH-sis",
      relatedTerms: ["Plasma Cells", "Light Chains", "Multiple Myeloma"],
      synonyms: ["Primary Amyloidosis"],
    },
    {
      term: "ATTR Amyloidosis",
      category: "Amyloidosis Types",
      definition:
        "Transthyretin (ATTR) amyloidosis occurs when the transthyretin protein becomes unstable and deposits as amyloid in tissues. It can be hereditary (ATTRv) or wild-type (ATTRwt).",
      pronunciation: "ay-tee-tee-ar am-uh-loy-DOH-sis",
      relatedTerms: ["ATTRv", "ATTRwt", "Transthyretin"],
      synonyms: ["TTR Amyloidosis"],
    },
    {
      term: "ATTRv",
      category: "Amyloidosis Types",
      definition:
        "Hereditary (variant) ATTR amyloidosis caused by mutations in the TTR gene. Symptoms typically appear between ages 50-80 and can affect the heart, nerves, and other organs.",
      pronunciation: "ay-tee-tee-ar variant",
      relatedTerms: ["ATTR Amyloidosis", "Familial Amyloid Polyneuropathy"],
      synonyms: ["Hereditary ATTR", "Familial Amyloidosis"],
    },
    {
      term: "ATTRwt",
      category: "Amyloidosis Types",
      definition:
        "Wild-type ATTR amyloidosis (formerly senile cardiac amyloidosis) is a non-hereditary form that primarily affects the heart. Most common in men over 70.",
      pronunciation: "ay-tee-tee-ar wild-type",
      relatedTerms: ["ATTR Amyloidosis", "Cardiac Amyloidosis"],
      synonyms: ["Wild-type ATTR", "Senile Cardiac Amyloidosis"],
    },
    {
      term: "AA Amyloidosis",
      category: "Amyloidosis Types",
      definition:
        "Secondary amyloidosis caused by chronic inflammatory conditions. The serum amyloid A protein forms amyloid deposits, primarily affecting the kidneys.",
      pronunciation: "ay-ay am-uh-loy-DOH-sis",
      relatedTerms: ["Chronic Inflammation", "Serum Amyloid A"],
      synonyms: ["Secondary Amyloidosis", "Reactive Amyloidosis"],
    },

    // Trial Phases
    {
      term: "Phase 1 Trial",
      category: "Trial Phases",
      definition:
        "First stage of testing in humans to evaluate safety, determine safe dosage range, and identify side effects. Usually involves 20-80 healthy volunteers or patients.",
      pronunciation: "fayz wun try-ul",
      relatedTerms: ["Phase 2 Trial", "Dose Escalation", "Safety Study"],
      synonyms: ["Phase I"],
    },
    {
      term: "Phase 2 Trial",
      category: "Trial Phases",
      definition:
        "Studies drug effectiveness and further evaluates safety. Involves larger groups (100-300 participants) with the condition being treated.",
      pronunciation: "fayz too try-ul",
      relatedTerms: ["Phase 1 Trial", "Phase 3 Trial", "Efficacy"],
      synonyms: ["Phase II"],
    },
    {
      term: "Phase 3 Trial",
      category: "Trial Phases",
      definition:
        "Large-scale testing (300-3,000 participants) to confirm effectiveness, monitor side effects, compare to standard treatments, and collect safety information.",
      pronunciation: "fayz three try-ul",
      relatedTerms: ["Phase 2 Trial", "Phase 4 Trial", "Pivotal Trial"],
      synonyms: ["Phase III", "Confirmatory Trial"],
    },
    {
      term: "Phase 4 Trial",
      category: "Trial Phases",
      definition:
        "Post-marketing studies conducted after FDA approval to gather additional information on long-term risks, benefits, and optimal use.",
      pronunciation: "fayz for try-ul",
      relatedTerms: ["Phase 3 Trial", "Post-Marketing Surveillance"],
      synonyms: ["Phase IV", "Post-Approval Study"],
    },

    // Medical Terms
    {
      term: "Biomarker",
      category: "Medical Terms",
      definition:
        "A measurable substance in the body that indicates disease presence, severity, or response to treatment. Examples include NT-proBNP for cardiac amyloidosis or free light chains for AL amyloidosis.",
      pronunciation: "BY-oh-mar-ker",
      relatedTerms: ["NT-proBNP", "Troponin", "Free Light Chains"],
      synonyms: ["Biological Marker"],
    },
    {
      term: "Biopsy",
      category: "Medical Terms",
      definition:
        "Removal and examination of tissue sample to diagnose disease. For amyloidosis, biopsies may be taken from fat pad, bone marrow, heart, kidney, or other affected organs.",
      pronunciation: "BY-op-see",
      relatedTerms: ["Congo Red Stain", "Pathology", "Tissue Sample"],
      synonyms: ["Tissue Biopsy"],
    },
    {
      term: "Congo Red Stain",
      category: "Medical Terms",
      definition:
        "Histological stain used to detect amyloid deposits in tissue samples. Amyloid appears red under normal light and shows apple-green birefringence under polarized light.",
      pronunciation: "KONG-go red stayn",
      relatedTerms: ["Biopsy", "Amyloid Deposits", "Polarized Light"],
      synonyms: ["Congo Red Staining"],
    },
    {
      term: "NT-proBNP",
      category: "Medical Terms",
      definition:
        "N-terminal pro-B-type natriuretic peptide - a blood test biomarker for heart failure and cardiac amyloidosis. Elevated levels indicate cardiac stress.",
      pronunciation: "en-tee-pro-bee-en-pee",
      relatedTerms: ["Biomarker", "Cardiac Amyloidosis", "Troponin"],
      synonyms: ["N-terminal proBNP", "BNP"],
    },
    {
      term: "Troponin",
      category: "Medical Terms",
      definition:
        "Protein released when heart muscle is damaged. Cardiac troponin levels help assess heart damage in cardiac amyloidosis.",
      pronunciation: "TROH-puh-nin",
      relatedTerms: ["NT-proBNP", "Cardiac Biomarker", "Myocardial Injury"],
      synonyms: ["Cardiac Troponin"],
    },
    {
      term: "Free Light Chains",
      category: "Medical Terms",
      definition:
        "Serum free light chains (FLC) test measures kappa and lambda light chains in blood. Abnormal ratios indicate plasma cell disorders like AL amyloidosis.",
      pronunciation: "free lyt chaynz",
      relatedTerms: ["AL Amyloidosis", "Kappa", "Lambda", "Plasma Cells"],
      synonyms: ["FLC", "Serum Free Light Chains"],
    },
    {
      term: "Echocardiogram",
      category: "Medical Terms",
      definition:
        'Ultrasound of the heart used to assess cardiac structure and function. Shows characteristic "sparkling" appearance in cardiac amyloidosis.',
      pronunciation: "ek-oh-KAR-dee-oh-gram",
      relatedTerms: ["Cardiac Amyloidosis", "Left Ventricular Hypertrophy"],
      synonyms: ["Echo", "Cardiac Ultrasound"],
    },
    {
      term: "Cardiac MRI",
      category: "Medical Terms",
      definition:
        "Magnetic resonance imaging of the heart. Late gadolinium enhancement (LGE) patterns help diagnose cardiac amyloidosis.",
      pronunciation: "KAR-dee-ak em-ar-eye",
      relatedTerms: ["Echocardiogram", "Late Gadolinium Enhancement"],
      synonyms: ["CMR", "Heart MRI"],
    },

    // Trial Terms
    {
      term: "Placebo",
      category: "Trial Terms",
      definition:
        "An inactive substance given to a control group in clinical trials. Helps determine if treatment effects are due to the drug itself or other factors.",
      pronunciation: "pluh-SEE-boh",
      relatedTerms: ["Control Group", "Double-Blind", "Randomization"],
      synonyms: ["Sugar Pill", "Inactive Treatment"],
    },
    {
      term: "Double-Blind",
      category: "Trial Terms",
      definition:
        "Study design where neither participants nor researchers know who receives the treatment or placebo, reducing bias.",
      pronunciation: "DUH-bul blind",
      relatedTerms: ["Placebo", "Randomization", "Blinding"],
      synonyms: ["Double-Blinded Study"],
    },
    {
      term: "Randomization",
      category: "Trial Terms",
      definition:
        "Process of randomly assigning participants to treatment or control groups to ensure unbiased comparison.",
      pronunciation: "ran-duh-mih-ZAY-shun",
      relatedTerms: ["Control Group", "Treatment Arm"],
      synonyms: ["Random Assignment"],
    },
    {
      term: "Inclusion Criteria",
      category: "Trial Terms",
      definition:
        "Characteristics that participants must have to be eligible for a clinical trial (e.g., diagnosis, age, disease stage).",
      pronunciation: "in-KLOO-zhun kry-TEER-ee-uh",
      relatedTerms: ["Exclusion Criteria", "Eligibility", "Enrollment"],
      synonyms: ["Eligibility Criteria"],
    },
    {
      term: "Exclusion Criteria",
      category: "Trial Terms",
      definition:
        "Characteristics that prevent someone from participating in a trial (e.g., other medical conditions, medications, pregnancy).",
      pronunciation: "ex-KLOO-zhun kry-TEER-ee-uh",
      relatedTerms: ["Inclusion Criteria", "Eligibility"],
      synonyms: ["Exclusionary Criteria"],
    },
    {
      term: "Informed Consent",
      category: "Trial Terms",
      definition:
        "Process ensuring participants understand trial purpose, procedures, risks, and benefits before agreeing to participate.",
      pronunciation: "in-FORMD kun-SENT",
      relatedTerms: ["Consent Form", "Study Protocol", "Ethics"],
      synonyms: ["Consent Process"],
    },
    {
      term: "Primary Endpoint",
      category: "Trial Terms",
      definition:
        "Main outcome measure used to determine if a treatment is effective. The primary result the study is designed to evaluate.",
      pronunciation: "PRY-mair-ee END-point",
      relatedTerms: ["Secondary Endpoint", "Outcome Measure"],
      synonyms: ["Primary Outcome"],
    },
    {
      term: "Secondary Endpoint",
      category: "Trial Terms",
      definition:
        "Additional outcomes measured in a trial beyond the primary endpoint. May include quality of life, biomarker changes, or other effects.",
      pronunciation: "SEK-un-dair-ee END-point",
      relatedTerms: ["Primary Endpoint", "Exploratory Endpoint"],
      synonyms: ["Secondary Outcome"],
    },
    {
      term: "Adverse Event",
      category: "Trial Terms",
      definition:
        "Any unfavorable medical occurrence during a trial, whether or not related to the treatment. Reported and monitored for safety.",
      pronunciation: "ad-VERS ih-VENT",
      relatedTerms: ["Side Effect", "Safety Monitoring"],
      synonyms: ["AE", "Adverse Reaction"],
    },
    {
      term: "Serious Adverse Event",
      category: "Trial Terms",
      definition:
        "Life-threatening event, hospitalization, disability, or death occurring during trial participation. Requires immediate reporting.",
      pronunciation: "SEER-ee-us ad-VERS ih-VENT",
      relatedTerms: ["Adverse Event", "Safety"],
      synonyms: ["SAE", "Serious AE"],
    },

    // Treatments
    {
      term: "Chemotherapy",
      category: "Treatments",
      definition:
        "Use of drugs to kill abnormal plasma cells in AL amyloidosis. Common regimens include bortezomib, cyclophosphamide, and dexamethasone.",
      pronunciation: "kee-moh-THER-uh-pee",
      relatedTerms: ["AL Amyloidosis", "Plasma Cells", "Bortezomib"],
      synonyms: ["Chemo"],
    },
    {
      term: "Autologous Stem Cell Transplant",
      category: "Treatments",
      definition:
        "Procedure using patient's own stem cells after high-dose chemotherapy. Can produce deep remissions in eligible AL amyloidosis patients.",
      pronunciation: "aw-TAH-luh-gus stem sel TRANS-plant",
      relatedTerms: ["AL Amyloidosis", "Stem Cells", "High-Dose Melphalan"],
      synonyms: ["ASCT", "Auto-SCT"],
    },
    {
      term: "Tafamidis",
      category: "Treatments",
      definition:
        "TTR stabilizer medication approved for ATTR cardiac amyloidosis. Prevents TTR protein from breaking apart and forming amyloid deposits.",
      pronunciation: "ta-FA-mi-dis",
      relatedTerms: ["ATTR Amyloidosis", "TTR Stabilizer"],
      synonyms: ["Vyndaqel", "Vyndamax"],
    },
    {
      term: "Gene Silencing",
      category: "Treatments",
      definition:
        "Therapy that reduces production of TTR protein in the liver. Includes patisiran and inotersen for ATTRv with polyneuropathy.",
      pronunciation: "jeen SY-len-sing",
      relatedTerms: ["ATTRv", "Patisiran", "Inotersen"],
      synonyms: ["RNA Interference", "RNAi"],
    },

    // Organs & Systems
    {
      term: "Cardiac Amyloidosis",
      category: "Organ Involvement",
      definition:
        "Amyloid deposits in the heart causing stiffening and thickening of heart muscle. Leads to heart failure, arrhythmias, and conduction abnormalities.",
      pronunciation: "KAR-dee-ak am-uh-loy-DOH-sis",
      relatedTerms: ["Heart Failure", "NT-proBNP", "Echocardiogram"],
      synonyms: ["Amyloid Heart Disease"],
    },
    {
      term: "Polyneuropathy",
      category: "Organ Involvement",
      definition:
        "Nerve damage affecting multiple peripheral nerves. Common in ATTRv, causing numbness, pain, and weakness in hands and feet.",
      pronunciation: "pol-ee-noo-ROP-uh-thee",
      relatedTerms: ["ATTRv", "Peripheral Neuropathy", "Autonomic Neuropathy"],
      synonyms: ["Peripheral Neuropathy"],
    },
    {
      term: "Autonomic Neuropathy",
      category: "Organ Involvement",
      definition:
        "Damage to nerves controlling automatic body functions. Causes orthostatic hypotension, GI issues, and erectile dysfunction.",
      pronunciation: "aw-toh-NAH-mik noo-ROP-uh-thee",
      relatedTerms: ["Polyneuropathy", "Orthostatic Hypotension"],
      synonyms: ["Autonomic Dysfunction"],
    },
    {
      term: "Renal Amyloidosis",
      category: "Organ Involvement",
      definition:
        "Amyloid deposits in kidneys causing proteinuria and progressive kidney failure. Most common in AL and AA amyloidosis.",
      pronunciation: "REE-nul am-uh-loy-DOH-sis",
      relatedTerms: ["Proteinuria", "Kidney Failure", "Nephrotic Syndrome"],
      synonyms: ["Kidney Amyloidosis"],
    },

    // Abbreviations
    {
      term: "GFR",
      category: "Abbreviations",
      definition:
        "Glomerular Filtration Rate - measure of kidney function. Decreased GFR indicates kidney damage.",
      pronunciation: "gee-ef-ar",
      relatedTerms: ["Kidney Function", "Creatinine"],
      synonyms: ["eGFR", "Estimated GFR"],
    },
    {
      term: "NYHA",
      category: "Abbreviations",
      definition:
        "New York Heart Association functional classification of heart failure severity (Class I-IV).",
      pronunciation: "ny-ha",
      relatedTerms: ["Heart Failure", "Cardiac Amyloidosis"],
      synonyms: ["NYHA Class"],
    },
    {
      term: "ICD",
      category: "Abbreviations",
      definition:
        "Implantable Cardioverter Defibrillator - device that monitors heart rhythm and delivers shocks if needed.",
      pronunciation: "eye-see-dee",
      relatedTerms: ["Arrhythmia", "Cardiac Amyloidosis"],
      synonyms: ["Defibrillator"],
    },
  ];

  const categories = [
    "all",
    "Amyloidosis Types",
    "Trial Phases",
    "Trial Terms",
    "Medical Terms",
    "Treatments",
    "Organ Involvement",
    "Abbreviations",
  ];

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  // Speech synthesis functions with limit checking
  const speak = (term, definition, pronunciation) => {
    // CHECK AUDIO LIMIT FIRST
    if (!isPremium && audioPlaysToday >= FREE_AUDIO_LIMIT) {
      setUpsellTrigger("audio_limit");
      setShowUpsellDialog(true);
      return;
    }

    console.log("🔊 Speak requested for:", term);

    if (!speechSupported) {
      setSnackbarMessage(
        "Your browser doesn't support text-to-speech. Please use Chrome, Edge, or Safari."
      );
      setShowSnackbar(true);
      return;
    }

    window.speechSynthesis.cancel();
    console.log("Cancelled previous speech");

    setTimeout(() => {
      const utterance = new SpeechSynthesisUtterance();
      utterance.text = `${term}. ${definition}`;
      utterance.rate = speechRate;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      utterance.lang = "en-US";

      const voices = window.speechSynthesis.getVoices();
      console.log(`Found ${voices.length} voices`);

      const preferredVoice =
        voices.find(
          (voice) =>
            voice.lang.startsWith("en") &&
            (voice.name.includes("Google") ||
              voice.name.includes("Microsoft") ||
              voice.name.includes("Samantha"))
        ) ||
        voices.find((voice) => voice.lang.startsWith("en-US")) ||
        voices.find((voice) => voice.lang.startsWith("en"));

      if (preferredVoice) {
        utterance.voice = preferredVoice;
        console.log(
          "✅ Using voice:",
          preferredVoice.name,
          preferredVoice.lang
        );
      }

      utterance.onstart = () => {
        console.log("✅ Speech STARTED");
        setIsSpeaking(true);
        setCurrentSpeakingTerm(term);
        setIsPaused(false);

        // Increment audio counter for free users
        if (!isPremium) {
          const today = new Date().toDateString();
          const newCount = audioPlaysToday + 1;
          setAudioPlaysToday(newCount);
          localStorage.setItem(
            "audioPlaysData",
            JSON.stringify({ date: today, count: newCount })
          );
        }
      };

      utterance.onend = () => {
        console.log("✅ Speech ENDED");
        setIsSpeaking(false);
        setCurrentSpeakingTerm(null);
        setIsPaused(false);
      };

      utterance.onerror = (event) => {
        console.error("❌ Speech synthesis error:", event);
        setIsSpeaking(false);
        setCurrentSpeakingTerm(null);
        setSnackbarMessage(
          `Audio error: ${event.error}. Try clicking again or refresh the page.`
        );
        setShowSnackbar(true);
      };

      console.log("🎤 Speaking text:", utterance.text.substring(0, 80) + "...");
      window.speechSynthesis.speak(utterance);
    }, 100);
  };

  const stopSpeaking = () => {
    console.log("⏹️ Stopping speech");
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    setCurrentSpeakingTerm(null);
    setIsPaused(false);
  };

  const pauseResumeSpeaking = () => {
    if (isPaused) {
      console.log("▶️ Resuming speech");
      window.speechSynthesis.resume();
      setIsPaused(false);
    } else {
      console.log("⏸️ Pausing speech");
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  };

  // Filter logic
  const filteredTerms = useMemo(() => {
    return glossaryData.filter((item) => {
      if (selectedCategory !== "all" && item.category !== selectedCategory) {
        return false;
      }

      if (
        selectedLetter !== "all" &&
        !item.term.toUpperCase().startsWith(selectedLetter)
      ) {
        return false;
      }

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          item.term.toLowerCase().includes(searchLower) ||
          item.definition.toLowerCase().includes(searchLower) ||
          item.synonyms?.some((syn) =>
            syn.toLowerCase().includes(searchLower)
          ) ||
          item.relatedTerms?.some((rel) =>
            rel.toLowerCase().includes(searchLower)
          )
        );
      }

      return true;
    });
  }, [searchTerm, selectedCategory, selectedLetter]);

  const sortedTerms = useMemo(() => {
    return [...filteredTerms].sort((a, b) => a.term.localeCompare(b.term));
  }, [filteredTerms]);

  const handleBookmark = (term) => {
    // CHECK BOOKMARK LIMIT FIRST
    if (
      !isPremium &&
      !bookmarkedTerms.has(term) &&
      bookmarkedTerms.size >= FREE_BOOKMARK_LIMIT
    ) {
      setUpsellTrigger("bookmark_limit");
      setShowUpsellDialog(true);
      return;
    }

    setBookmarkedTerms((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(term)) {
        newSet.delete(term);
        setSnackbarMessage(`Removed "${term}" from bookmarks`);
      } else {
        newSet.add(term);
        setSnackbarMessage(`Added "${term}" to bookmarks`);
      }
      setShowSnackbar(true);
      return newSet;
    });
  };

  const handleAccordionChange = (term) => (event, isExpanded) => {
    if (isExpanded) {
      setRecentTerms((prev) => {
        const updated = [term, ...prev.filter((t) => t !== term)].slice(0, 5);
        localStorage.setItem("recentGlossaryTerms", JSON.stringify(updated));
        return updated;
      });
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Amyloidosis Types": "#e91e63",
      "Trial Phases": "#9c27b0",
      "Trial Terms": "#3f51b5",
      "Medical Terms": "#2196f3",
      Treatments: "#009688",
      "Organ Involvement": "#ff9800",
      Abbreviations: "#795548",
    };
    return colors[category] || "#1A5EA8";
  };

  const getTermOfTheDay = () => {
    const dayOfYear = Math.floor(
      (new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000
    );
    return glossaryData[dayOfYear % glossaryData.length];
  };

  const termOfDay = getTermOfTheDay();

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <MenuBookIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Clinical Trial Glossary
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Understand medical and trial terminology with audio pronunciations
        </Typography>
      </Box>

      {/* Browser Support Warning */}
      {!speechSupported && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Audio not supported:</strong> Your browser doesn't support
          text-to-speech. Please use Chrome, Edge, Safari, or Firefox for audio
          pronunciations.
        </Alert>
      )}

      {/* Term of the Day */}
      <Card
        elevation={3}
        sx={{
          mb: 3,
          background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
          color: "white",
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <StarIcon />
            <Typography variant="h6" fontWeight="bold">
              📖 Term of the Day
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
            {termOfDay.term}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.95 }}>
            {termOfDay.definition.substring(0, 150)}...
          </Typography>
          <Button
            variant="contained"
            size="small"
            sx={{
              bgcolor: "white",
              color: "#f5576c",
              "&:hover": { bgcolor: "#f5f5f5" },
            }}
            onClick={() => setSearchTerm(termOfDay.term)}
          >
            Learn More
          </Button>
        </CardContent>
      </Card>

      {/* Recently Viewed */}
      {recentTerms.length > 0 && (
        <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <HistoryIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight="600">
              📚 Recently Viewed:
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {recentTerms.map((term, idx) => (
              <Chip
                key={idx}
                label={term}
                onClick={() => setSearchTerm(term)}
                size="small"
                sx={{ mb: 0.5, cursor: "pointer" }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>{glossaryData.length} terms available.</strong> Click the 🔊
        speaker icon to hear pronunciations. Use search, filters, and alphabet
        navigation to find definitions.
        {bookmarkedTerms.size > 0 &&
          ` You have ${bookmarkedTerms.size} bookmarked term${
            bookmarkedTerms.size > 1 ? "s" : ""
          }.`}
      </Alert>

      {/* Free tier usage banner */}
      {!isPremium && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setUpsellTrigger("general");
                setShowUpsellDialog(true);
              }}
            >
              Upgrade
            </Button>
          }
        >
          <strong>Free Plan:</strong> {audioPlaysToday}/{FREE_AUDIO_LIMIT} audio
          plays today • {bookmarkedTerms.size}/{FREE_BOOKMARK_LIMIT} bookmarks
          used • {Math.min(sortedTerms.length, FREE_TERMS_LIMIT)}/
          {glossaryData.length} terms available
        </Alert>
      )}

      {/* Search Bar */}
      <Paper elevation={3} sx={{ mb: 3, p: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search terms, definitions, or synonyms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Speech Speed</InputLabel>
              <Select
                value={speechRate}
                onChange={(e) => setSpeechRate(e.target.value)}
                label="Speech Speed"
                startAdornment={<SpeedIcon fontSize="small" sx={{ mr: 1 }} />}
              >
                <MenuItem value={0.6}>Slow (0.6x)</MenuItem>
                <MenuItem value={0.8}>Normal (0.8x)</MenuItem>
                <MenuItem value={1.0}>Fast (1.0x)</MenuItem>
                <MenuItem value={1.2}>Very Fast (1.2x)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Premium CTA Banner */}
      {!isPremium && (
        <Paper
          elevation={3}
          sx={{
            mb: 3,
            p: 3,
            background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            color: "white",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                🎧 Unlock Premium Features
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95 }}>
                Professional audio, unlimited bookmarks, AI glossary, offline
                access, +100 terms!
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: { xs: "left", md: "right" } }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  setUpsellTrigger("general");
                  setShowUpsellDialog(true);
                }}
                sx={{
                  bgcolor: "white",
                  color: "#1A5EA8",
                  fontWeight: "bold",
                  "&:hover": { bgcolor: "#f5f5f5" },
                }}
              >
                Upgrade to Premium
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Global Speech Controls */}
      {isSpeaking && (
        <Paper
          elevation={3}
          sx={{
            mb: 3,
            p: 2,
            bgcolor: "primary.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <VolumeUpIcon color="primary" />
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Now Playing: {currentSpeakingTerm}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isPaused ? "Paused" : "Speaking..."}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title={isPaused ? "Resume" : "Pause"}>
              <IconButton onClick={pauseResumeSpeaking} color="primary">
                {isPaused ? <PlayCircleIcon /> : <PauseCircleIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Stop">
              <IconButton onClick={stopSpeaking} color="error">
                <VolumeOffIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      )}

      {/* Category Tabs */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={selectedCategory}
          onChange={(e, newValue) => setSelectedCategory(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          {categories.map((cat) => (
            <Tab
              key={cat}
              label={cat === "all" ? "All Categories" : cat}
              value={cat}
              sx={{ textTransform: "none", fontWeight: 500 }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Alphabet Navigation */}
      <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <FilterListIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" color="text.secondary">
            Jump to Letter:
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          <Chip
            label="All"
            onClick={() => setSelectedLetter("all")}
            color={selectedLetter === "all" ? "primary" : "default"}
            size="small"
          />
          {alphabet.map((letter) => (
            <Chip
              key={letter}
              label={letter}
              onClick={() => setSelectedLetter(letter)}
              color={selectedLetter === letter ? "primary" : "default"}
              size="small"
            />
          ))}
        </Box>
      </Paper>

      {/* Results Count */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {sortedTerms.length} of {glossaryData.length} terms
          {searchTerm && ` matching "${searchTerm}"`}
        </Typography>
        {!isPremium && (
          <Chip
            icon={<LockIcon />}
            label={`${
              glossaryData.length - FREE_TERMS_LIMIT
            } Premium Terms Locked`}
            size="small"
            color="warning"
            onClick={() => {
              setUpsellTrigger("term_locked");
              setShowUpsellDialog(true);
            }}
            sx={{ cursor: "pointer" }}
          />
        )}
      </Box>

      {/* Terms List */}
      {sortedTerms.length > 0 ? (
        <Box>
          {sortedTerms.map((item, index) => (
            <Accordion
              key={index}
              elevation={2}
              onChange={handleAccordionChange(item.term)}
              sx={{
                mb: 2,
                "&:before": { display: "none" },
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flex: 1,
                    pr: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        {item.term}
                      </Typography>
                      <Chip
                        label={item.category}
                        size="small"
                        sx={{
                          bgcolor: `${getCategoryColor(item.category)}15`,
                          color: getCategoryColor(item.category),
                          fontWeight: 600,
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>
                    {item.pronunciation && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                      >
                        {item.pronunciation}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBookmark(item.term);
                    }}
                  >
                    {bookmarkedTerms.has(item.term) ? (
                      <BookmarkIcon color="primary" />
                    ) : (
                      <BookmarkBorderIcon />
                    )}
                  </IconButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Divider sx={{ mb: 2 }} />

                {/* Definition */}
                <Typography variant="body1" paragraph>
                  {item.definition}
                </Typography>

                {/* Pronunciation Button */}
                {item.pronunciation && (
                  <Box sx={{ mb: 2 }}>
                    <Tooltip
                      title={
                        speechSupported
                          ? "Hear pronunciation"
                          : "Audio not supported in your browser"
                      }
                    >
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={
                            isSpeaking && currentSpeakingTerm === item.term ? (
                              isPaused ? (
                                <PlayCircleIcon />
                              ) : (
                                <PauseCircleIcon />
                              )
                            ) : (
                              <VolumeUpIcon />
                            )
                          }
                          onClick={() => {
                            if (
                              isSpeaking &&
                              currentSpeakingTerm === item.term
                            ) {
                              pauseResumeSpeaking();
                            } else {
                              speak(
                                item.term,
                                item.definition,
                                item.pronunciation
                              );
                            }
                          }}
                          disabled={!speechSupported}
                          color={
                            isSpeaking && currentSpeakingTerm === item.term
                              ? "error"
                              : "primary"
                          }
                        >
                          {isSpeaking && currentSpeakingTerm === item.term
                            ? isPaused
                              ? "Resume"
                              : "Pause"
                            : "Hear Pronunciation"}
                        </Button>
                      </span>
                    </Tooltip>
                    {isSpeaking && currentSpeakingTerm === item.term && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VolumeOffIcon />}
                        onClick={stopSpeaking}
                        color="error"
                        sx={{ ml: 1 }}
                      >
                        Stop
                      </Button>
                    )}
                  </Box>
                )}

                {/* Synonyms */}
                {item.synonyms && item.synonyms.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Also Known As:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {item.synonyms.map((syn, idx) => (
                        <Chip
                          key={idx}
                          label={syn}
                          size="small"
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Related Terms */}
                {item.relatedTerms && item.relatedTerms.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Related Terms:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {item.relatedTerms.map((rel, idx) => (
                        <Chip
                          key={idx}
                          label={rel}
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={() => setSearchTerm(rel)}
                          sx={{ mb: 0.5, cursor: "pointer" }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Paper
          elevation={2}
          sx={{
            textAlign: "center",
            py: 8,
            px: 4,
            bgcolor: "background.default",
          }}
        >
          <SearchIcon sx={{ fontSize: 80, color: "text.disabled", mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No terms found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Try adjusting your search or filters
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              setSearchTerm("");
              setSelectedCategory("all");
              setSelectedLetter("all");
            }}
            startIcon={<ClearIcon />}
          >
            Clear All Filters
          </Button>
        </Paper>
      )}

      {/* Disclaimer */}
      <Paper elevation={1} sx={{ mt: 4, p: 3, bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Disclaimer:</strong> This glossary is for educational purposes
          only and should not replace professional medical advice. Always
          consult with your healthcare provider for medical guidance specific to
          your condition. Audio pronunciations are computer-generated and may
          not reflect exact medical pronunciation.
        </Typography>
      </Paper>

      {/* Premium Upsell Dialog — not rendered on native iOS. The
          underlying /pricing → /checkout flow is a non-functional Stripe
          stub (see App.js BlockOnIOS comment); showing an upgrade prompt
          that leads nowhere is worse than not showing it, and a reachable
          external-payment-style UI for digital content risks an App
          Store Guideline 3.1.1 rejection. */}
      {!(Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') && (
        <PremiumUpsellDialog
          open={showUpsellDialog}
          onClose={() => setShowUpsellDialog(false)}
          trigger={upsellTrigger}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
};

export default GlossaryPage;